
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Check, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ImportEventsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: (events: any[]) => void;
}

const ImportEventsDialog: React.FC<ImportEventsDialogProps> = ({
  open,
  onOpenChange,
  onImportComplete
}) => {
  const [activeTab, setActiveTab] = useState('songkick');
  const [songkickUsername, setSongkickUsername] = useState('');
  const [eventbriteToken, setEventbriteToken] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importedEvents, setImportedEvents] = useState<any[]>([]);
  const [importStatus, setImportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  
  const handleImport = () => {
    setIsImporting(true);
    setImportStatus('loading');
    
    // Simulate API fetch with timeout
    setTimeout(() => {
      // Mock data based on platform
      const mockEvents = activeTab === 'songkick' 
        ? [
            { 
              id: 'sk1', 
              name: 'Live at The Roxy', 
              date: '2023-12-15',
              venue: 'The Roxy', 
              location: 'Los Angeles, CA',
              source: 'songkick'
            },
            { 
              id: 'sk2', 
              name: 'Downtown Music Festival', 
              date: '2023-12-20',
              venue: 'Civic Center', 
              location: 'San Francisco, CA',
              source: 'songkick'
            }
          ]
        : [
            { 
              id: 'eb1', 
              name: 'Winter Showcase', 
              date: '2023-12-18',
              venue: 'The Echo', 
              location: 'Los Angeles, CA',
              ticketsSold: 45,
              capacity: 120,
              source: 'eventbrite'
            },
            { 
              id: 'eb2', 
              name: 'New Year Celebration', 
              date: '2023-12-31',
              venue: 'The Troubadour', 
              location: 'West Hollywood, CA',
              ticketsSold: 85,
              capacity: 150,
              source: 'eventbrite'
            }
          ];
          
      setImportedEvents(mockEvents);
      setImportStatus('success');
      setIsImporting(false);
    }, 2000);
  };
  
  const handleConfirmImport = () => {
    onImportComplete(importedEvents);
    setImportStatus('idle');
    setImportedEvents([]);
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Import Events</DialogTitle>
          <DialogDescription>
            Import your gigs from third-party platforms to keep everything in sync
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="songkick">Songkick</TabsTrigger>
            <TabsTrigger value="eventbrite">Eventbrite</TabsTrigger>
          </TabsList>
          
          <TabsContent value="songkick" className="mt-4 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                <img 
                  src="https://www.songkick.com/static/images/favicon-196.png" 
                  alt="Songkick" 
                  className="h-6 w-6"
                />
              </div>
              <div>
                <h4 className="text-sm font-medium">Import from Songkick</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Import your artist events from Songkick by entering your artist username
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="songkick-username">Songkick Username</Label>
              <div className="flex gap-2">
                <Input 
                  id="songkick-username"
                  placeholder="your-artist-name"
                  value={songkickUsername}
                  onChange={(e) => setSongkickUsername(e.target.value)}
                  disabled={importStatus === 'loading' || importStatus === 'success'}
                />
                <Button 
                  variant="default" 
                  onClick={handleImport} 
                  disabled={!songkickUsername || importStatus === 'loading' || importStatus === 'success'}
                >
                  {importStatus === 'loading' && activeTab === 'songkick' && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Import
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                This will import your upcoming performances listed on Songkick
              </p>
            </div>
            
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Don't have a Songkick account?</span>
              <a 
                href="https://www.songkick.com/artist-sign-up" 
                target="_blank" 
                rel="noreferrer"
                className="text-primary hover:underline flex items-center gap-0.5"
              >
                Create one <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </TabsContent>
          
          <TabsContent value="eventbrite" className="mt-4 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                <img 
                  src="https://www.eventbrite.com/favicon.ico" 
                  alt="Eventbrite" 
                  className="h-6 w-6"
                />
              </div>
              <div>
                <h4 className="text-sm font-medium">Import from Eventbrite</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect to Eventbrite to import your events and track ticket sales
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="eventbrite-token">Eventbrite API Key</Label>
              <div className="flex gap-2">
                <Input 
                  id="eventbrite-token"
                  type="password"
                  placeholder="Your private API key"
                  value={eventbriteToken}
                  onChange={(e) => setEventbriteToken(e.target.value)}
                  disabled={importStatus === 'loading' || importStatus === 'success'}
                />
                <Button 
                  variant="default" 
                  onClick={handleImport} 
                  disabled={!eventbriteToken || importStatus === 'loading' || importStatus === 'success'}
                >
                  {importStatus === 'loading' && activeTab === 'eventbrite' && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Import
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                We'll sync ticket sales data in real-time to show on your gig calendar
              </p>
            </div>
            
            <div className="bg-muted p-3 rounded-md text-xs text-muted-foreground">
              <p className="font-medium mb-1">Benefits of Eventbrite integration:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Track ticket sales in real-time</li>
                <li>Automatic updates when events change</li>
                <li>Sync attendee data for marketing</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
        
        {importStatus === 'success' && (
          <>
            <Separator />
            
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-500">
                <Check className="h-5 w-5" />
                <span className="font-medium">Found {importedEvents.length} events to import</span>
              </div>
              
              <div className="max-h-40 overflow-y-auto space-y-2 pr-2">
                {importedEvents.map(event => (
                  <div 
                    key={event.id} 
                    className="p-2 border rounded-md flex justify-between items-center"
                  >
                    <div>
                      <p className="text-sm font-medium">{event.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.venue}, {event.location} | {new Date(event.date).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant={event.source === 'songkick' ? 'neon-purple' : 'neon-pink'}>
                      {event.source}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              if (importStatus === 'success') {
                setImportStatus('idle');
                setImportedEvents([]);
              } else {
                onOpenChange(false);
              }
            }}
          >
            {importStatus === 'success' ? 'Cancel' : 'Close'}
          </Button>
          
          {importStatus === 'success' && (
            <Button onClick={handleConfirmImport}>
              Import {importedEvents.length} Events
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportEventsDialog;
